'use client';

import React, { useEffect, useRef, useState } from 'react';
import { gridMetrics } from './HeroGrid';

/** A cleared rectangle. On desktop it covers whole grid cells with crisp edges;
 *  on mobile a single soft-edged rectangle backs the whole content stack. */
type Band = { top: number; left: number; width: number; height: number; feather?: boolean };

/** Below this width the grid is too tight to lay content on, so the hero uses a
 *  natural stack and a single soft clearing instead of the grid-paper one. Matches
 *  Tailwind's `md` breakpoint. */
const MOBILE_MAX = 768;

/** Soft feather (px) for the mobile clearing's edges. */
const FEATHER = 22;
const featherMask =
  `linear-gradient(to right, transparent 0, #000 ${FEATHER}px, #000 calc(100% - ${FEATHER}px), transparent 100%),` +
  `linear-gradient(to bottom, transparent 0, #000 ${FEATHER}px, #000 calc(100% - ${FEATHER}px), transparent 100%)`;

/**
 * Paints grid-snapped "clearings" behind the hero content: opaque rectangles the
 * colour of the page background (matching app/globals.css `body`) that hide the
 * body grid only over the *complete* cells that actually hold content. Every cell
 * is therefore either fully cleared (content) or fully drawn (empty) — no
 * half-covered squares — so the grid reads like grid paper the content is laid out
 * on. Empty cells, both the rows between blocks and the margins beside narrower
 * blocks (e.g. the badge), keep their grid.
 *
 * For each content element we find the grid cells it touches: its rows (in
 * document-y, to line up with the body grid's horizontal lines) and a symmetric
 * horizontal span — the half-extent from the viewport centre, snapped out to a
 * grid line and mirrored, so the clearing can never drift left/right. Rows that
 * share the same horizontal span merge into one rectangle; a row with no content
 * is simply skipped, leaving the grid visible.
 *
 * Measurement is scoped to the hero container ([data-hero-content]) so matching
 * text elsewhere on the page (e.g. a similar label in another section) can't pollute the
 * geometry. Re-measured on resize, font load and content reflow.
 */
const HeroContentMask: React.FC<{
  /** The hero's content container. Scoped per-instance so multiple heroes on one
   *  page (e.g. copy-comparison variants) each clear their own content. */
  contentRef: React.RefObject<HTMLElement | null>;
}> = ({ contentRef }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [bands, setBands] = useState<Band[]>([]);

  useEffect(() => {
    const compute = () => {
      const root = ref.current;
      const content = contentRef.current;
      if (!root || !content) return;

      const w = document.documentElement.clientWidth;
      const { cell, offsetX } = gridMetrics(w);
      const center = w / 2;
      const scrollY = window.scrollY;
      const layer = root.getBoundingClientRect();
      const layerDocTop = layer.top + scrollY;

      // Collect tight content rects, scoped to the hero. Text uses
      // Range.getClientRects() so a centred <h1>/<p> contributes its actual line
      // widths; boxes (demo card, badge, each logo) use their bounding rect.
      const rects: DOMRect[] = [];
      const addText = (el: Element | null) => {
        if (!el) return;
        const range = document.createRange();
        range.selectNodeContents(el);
        const rs = Array.from(range.getClientRects());
        if (rs.length) rs.forEach((r) => rects.push(r as DOMRect));
        else rects.push(el.getBoundingClientRect());
      };
      const addBox = (el: Element | null) => {
        if (el) rects.push(el.getBoundingClientRect());
      };
      const byText = (sel: string, needle: RegExp): Element | null => {
        const matches = Array.from(content.querySelectorAll(sel)).filter((e) =>
          needle.test(e.textContent || ''),
        );
        if (!matches.length) return null;
        return matches.reduce((best, e) => {
          const a = e.getBoundingClientRect();
          const b = best.getBoundingClientRect();
          return a.width * a.height < b.width * b.height ? e : best;
        });
      };

      addBox(byText('div', /Smart Routing Rules/));
      addText(content.querySelector('h1'));
      addText(content.querySelector('p[class*="max-w-2xl"]'));
      addBox(content.querySelector('[class*="max-w-4xl"]'));
      addText(byText('p', /Running links for/i));
      const logos = content.querySelector('[data-customer-logos]');
      if (logos) Array.from(logos.children).forEach((c) => addBox(c));

      const valid = rects.filter((r) => r.width && r.height);
      if (!valid.length) {
        setBands([]);
        return;
      }

      // Mobile: one soft, symmetric rectangle behind the whole stack — no grid
      // snapping (the content doesn't follow the grid here).
      if (w < MOBILE_MAX) {
        const top = Math.min(...valid.map((r) => r.top));
        const bottom = Math.max(...valid.map((r) => r.bottom));
        const half =
          Math.max(...valid.map((r) => Math.max(center - r.left, r.right - center))) + 18;
        const left = Math.max(0, center - half);
        setBands([
          {
            left: left - layer.left,
            width: Math.min(w, 2 * half),
            top: top - 16 - layer.top,
            height: bottom - top + 32,
            feather: true,
          },
        ]);
        return;
      }

      // For each row that holds content, track the widest half-extent from centre.
      const rowHalf = new Map<number, number>();
      for (const r of rects) {
        if (!r.width || !r.height) continue;
        const half = Math.max(center - r.left, r.right - center);
        if (half <= 0) continue;
        const r0 = Math.floor((r.top + scrollY) / cell);
        const r1 = Math.ceil((r.bottom + scrollY) / cell);
        for (let row = r0; row < r1; row++) {
          rowHalf.set(row, Math.max(rowHalf.get(row) ?? 0, half));
        }
      }
      if (!rowHalf.size) {
        setBands([]);
        return;
      }

      // Snap each content row to whole cells: left edge out to a grid line, right
      // mirrored across the centre (→ symmetric, both edges on grid lines).
      const snapLeft = (half: number) => {
        const n = Math.floor((center - half - offsetX) / cell);
        return Math.round(offsetX + n * cell);
      };
      const rowLeft = new Map<number, number>();
      for (const [row, half] of rowHalf) rowLeft.set(row, snapLeft(half));

      // Merge vertically-adjacent rows that share the same horizontal span into one
      // rectangle. Rows with no content break the run, so the grid survives there.
      const rows = Array.from(rowLeft.keys()).sort((a, b) => a - b);
      const out: Band[] = [];
      let start = rows[0];
      let prev = rows[0];
      let left = rowLeft.get(rows[0])!;
      const flush = (endRow: number, l: number) => {
        // Inset the top-left by 1px so the grid lines that border the cleared
        // cells survive (the body grid draws each line at a cell's top-left edge,
        // which a flush rect would otherwise cover — keeping the right/bottom
        // border lines but eating the top/left ones, i.e. an asymmetric result
        // for centred content). Right/bottom edges are left on the grid lines.
        out.push({
          left: l - layer.left + 1,
          width: w - 2 * l - 1,
          top: Math.round(start * cell - layerDocTop) + 1,
          height: (endRow + 1 - start) * cell - 1,
        });
      };
      for (let i = 1; i < rows.length; i++) {
        const row = rows[i];
        const l = rowLeft.get(row)!;
        if (row === prev + 1 && l === left) {
          prev = row;
        } else {
          flush(prev, left);
          start = row;
          prev = row;
          left = l;
        }
      }
      flush(prev, left);
      setBands(out);
    };

    compute();
    // Re-measure once fonts settle (headline metrics shift the content box).
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
    fonts?.ready.then(compute).catch(() => {});

    let raf = 0;
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(compute);
    };
    window.addEventListener('resize', schedule);
    const content = contentRef.current;
    const ro = content ? new ResizeObserver(schedule) : null;
    if (content && ro) ro.observe(content);

    return () => {
      window.removeEventListener('resize', schedule);
      cancelAnimationFrame(raf);
      ro?.disconnect();
    };
  }, []);

  return (
    <div ref={ref} aria-hidden className="absolute inset-0 overflow-hidden pointer-events-none">
      {bands.map((b, i) => (
        <div
          key={i}
          className="absolute bg-[#f8fafc]"
          style={{
            left: b.left,
            top: b.top,
            width: b.width,
            height: b.height,
            ...(b.feather
              ? {
                  WebkitMaskImage: featherMask,
                  maskImage: featherMask,
                  WebkitMaskComposite: 'source-in',
                  maskComposite: 'intersect',
                }
              : null),
          }}
        />
      ))}
    </div>
  );
};

export default HeroContentMask;
